import {
  Account,
  Country,
  Currency,
  Pagination,
  TransactionTypeEnum,
  TransactionWarning,
} from '@ui/src';
import { FeeItem } from './tariff/types';

export enum ApplicationSchemeType {
  INDIVIDUAL = 'INDIVIDUAL',
  CORPORATE = 'CORPORATE',
  COMPANY_OFFICIAL = 'COMPANY_OFFICIAL',
}

export enum NatureOfControlEnum {
  DIRECT_CONTROL = 'DIRECT_CONTROL',
  INDIRECT_CONTROL = 'INDIRECT_CONTROL',
}

export enum CofApprovalStatus {
  INPROCESS = 'INPROCESS',
  ACCEPTED = 'ACCEPTED',
  DECLINED = 'DECLINED',
}

export enum SignatureType {
  NONE = 'NONE',
  SINGLE = 'SINGLE',
}

export enum AccessRight {
  NONE = 'NONE',
  VIEWER = 'VIEWER',
}

export interface AccountLogs {
  action: string;
  comment: string;
  createdAt: string;
  id: string;
}

export interface Comment {
  id?: string;
  user: string;
  createdDate: string;
  text?: string;
  document?: {
    link?: string;
    name: string;
  };
}

export enum CurrencyForWritingOffFee {
  IN_TRANSACTION_CURRENCY = 'in Transaction currency',
  IN_FEE_CURRENCY = 'in Fee currency',
}

export enum PspPartnerPaymentType {
  FASTER_PAYMENT = 'FASTER_PAYMENT',
  BACS = 'BACS',
  SEPA_SCT_INST = 'SEPA_SCT_INST',
  SEPA_SCT = 'SEPA_SCT',
  TARGET_2 = 'TARGET_2',
  SWIFT = 'SWIFT',
  FEDWIRE = 'FEDWIRE',
  CRYPTO_ASSET_PAYMENT = 'CRYPTO_ASSET_PAYMENT',
  CASH_OVER_COUNTER = 'CASH_OVER_COUNTER',
}

export interface FeeListItem {
  id?: string;
  name: string;
  value: string;
  type?: CurrencyForWritingOffFee;
}

export interface FeeTypeItem {
  title?: string;
  id?: string;
  list: FeeListItem[];
  type?: string;
  subType?: string;
}

export enum FeeTypeEnum {
  MANUAL = 'MANUAL',
  REGULAR = 'REGULAR',
  EXCHANGE = 'EXCHANGE',
  TRANSACTION = 'TRANSACTION',
}

export interface SuspiciousTransaction {
  comments?: Comment[];
  canBeClosed?: boolean;
}

export interface CardsTransactions {
  id: string;
  date: string;
  description: string;
  transaction?: 'canceled' | 'good' | 'warning';
  currency: string;
  amount: string;
  merchantName: string;
  orderDetails: string;
  render?: Record<string, React.JSX.Element>;
}

export enum AccountTransactionType {
  INCOMING = 'Incoming',
  OUTGOING = 'Outgoing',
  INTERNAL = 'Internal',
  EXCHANGE = 'Exchange',
  GENERAL_LEDGER = 'General Ledger',
}

export interface AccountTransaction {
  id: string;
  date: string;
  description: AccountTransactionType;
  currency: string;
  debit: string;
  amount: string;
  balance: string;
  suspiciousTransaction?: boolean;
  render?: Record<string, React.JSX.Element>;
}

export interface CardTransaction {
  id: string;
  transaction?: string;
  description: string;
  currency: string;
  amountBc: string;
  amount: string;
  merchantName: string;
  terminalId: string;
  date: string;
}

export interface AmlLog {
  id: string;
  user: string;
  date: string;
  event: string;
  comment?: string;
}

export interface CustomFeeFormValues {
  feeId: string;
  name: string;
  feeTargetAccountNumber?: string;
  feePercentAmount?: number;
  feeFixedAmount?: number;
  feePercentMinAmount?: number;
  feePercentMaxAmount?: number;
  feeAmountType: string;
  feeType?: string;
  feeCascadeCurrenciesIds?: string[];
  feeCurrencyId?: string;
}

export enum ClientScoreTypeEnum {
  AUTO = 'AUTO',
  MANUAL = 'MANUAL',
}

export interface CustomerScore {
  id: string;
  createdAt: string;
  updatedAt: string;
  organizationId: string;
  applicationId: string;
  totalRiskScore: number;
  geographicalRiskTotal: number;
  countryRisk: number;
  countryId: string;
  nationalityRisk: number;
  nationalityId: string;
  deliveryChannelRisk: number;
  deliveryChannelId: string;
  clientActivityRisk: number;
  clientActivityId: string;
  countryOfIncorporationRisk?: number;
  countryOfIncorporationId?: string;
  sourcesOfFundsRiskTotal: number;
  sourcesOfFundsRiskItems: { id: string; name: string; value: number }[];
  productsRiskTotal: number;
  productsRiskItems: { id: string; name: string; value: number }[];
  sicRiskTotal: number;
  sicRiskItems: { id: string; name: string; value: number }[];
  officialRiskItems: { id: string; name: string; value: string }[];
  officialRiskTotal: number;
  monthlyTurnoverRangeRisk: number;
  monthlyTurnoverRangeId: string;
  monthlyTransactionRangeRisk: number;
  monthlyTransactionRangeId: string;
  dateOfIncorporationRisk: number;
  scoreCalculatedAt: string;
  scoreAcceptedAt: string;
  country: number;
  nationality: { id: string; name: string; value: number };
  deliveryChannel: string;
  clientActivity: string;
  monthlyTurnoverRange: string;
  monthlyTransactionRange: { id: string; name: string; value: number };
  dateOfIncorporation: string;
  clientScoreType: ClientScoreTypeEnum;
  employeeId?: string;
  order?: number;
}

export enum NewApplicationStatus {
  INCOMPLETE = 'INCOMPLETE',
  ACCEPTED = 'ACCEPTED',
  DECLINED = 'DECLINED',
  READY_TO_REVIEW = 'READY_TO_REVIEW',
  REVIEW_IN_PROGRESS = 'REVIEW_IN_PROGRESS',
}

export enum OrganizationType {
  INDIVIDUAL = 'INDIVIDUAL',
  CORPORATE = 'CORPORATE',
  COMPANY_OFFICIAL = 'COMPANY_OFFICIAL',
}

export interface TotalRiskLevelValues {
  good: number[];
  medium: number[];
}

export enum EmploymentStatus {
  EMPLOYEE = 'EMPLOYEE',
  SELF_EMPLOYED = 'SELF_EMPLOYED',
  STUDENT = 'STUDENT',
  RETIREE = 'RETIREE',
  UNEMPLOYED = 'UNEMPLOYED',
  OTHER = 'OTHER',
}

export enum CustomerStatus {
  ACTIVE = 'ACTIVE',
  SUSPENDED = 'SUSPENDED',
  DISABLED = 'DISABLED',
  CLOSED = 'CLOSED',
}

export interface CustomerRanges {
  monthlyMaxValueIncomingPaymentRangeId?: string;
  monthlyMaxValueOutgoingPaymentRangeId?: string;
  monthlyNumberIncomingTransactionsRangeId?: string;
  monthlyNumberOutgoingTransactionsRangeId?: string;
  monthlyTurnoverRangeId?: string;
  monthlyValueIncomingTransactionsRangeId?: string;
  monthlyValueOutgoingTransactionsRangeId?: string;
}

export interface SupplierCustomersProps {
  applicationId: string;
  monthlyTurnoverRangeId: string;
  name: string;
  id: string;
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string;
}

export interface Company {
  authLevel: string;
  companyOfficialType: string;
  createdAt: string;
  id: string;
  rejectedAt: string;
  archivedAt: string;
  archivedBy: string;
  rejectedBy: string;
  approvedBy: string;
  approvedAt: string;
  rejectionReason?: string;
  natureOfControl: NatureOfControlEnum;
  organizationId: string;
  signatureType: SignatureType;
  accessRight?: string;
  percentage: string;
  position: string;
  status: string;
  havingControl?: boolean;
  employeeName?: string;
  user?: {
    profile?: {
      firstName: string;
      lastName: string;
    };
  };
  organization: {
    companyName: string;
    id: string;
  };
  render?: Record<string, React.JSX.Element>;
}

export enum CofStatus {
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  ARCHIVED = 'ARCHIVED',
  PENDING_REVIEW = 'PENDING_REVIEW',
}

export interface Questionnarie {
  id?: string;
  nationalInsuranceNumberOfDirectors?: string;
  lastThreeYearsAddressHistory?: string;
  criminalConvictedOfAny?: string | boolean;
  criminalReceivedACautionForAny?: string | boolean;
  criminalArrestedOrChargedWithOrBeenInvolved?: string | boolean;
  criminalSubjectToAnyCurrentCriminalInvestigation?: string | boolean;
  criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenConvicted?:
    string | boolean;
  criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenOrdered?:
    string | boolean;
  criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenInvolved?:
    string | boolean;
  civilFoundLiableForFraud?: string | boolean;
  civilSubjectToAJudgmentDebt?: string | boolean;
  civilInvolvedInAnyCivilProceedings?: string | boolean;
  civilYouHeldAPositionOfInfluence?: string | boolean;
  civilFiledForBankruptcy?: string | boolean;
  civilSubjectToABankruptcy?: string | boolean;
  businessAndEmploymentDismissedOrAskedTo?: string | boolean;
  businessAndEmploymentDisqualifiedFromActingAsADirector?: string | boolean;
  businessAndEmploymentTheSubjectOfAnyDisciplinaryProceedings?:
    string | boolean;
  regulatoryAnAuthorizationFromARegulatoryBodyRefused?: string | boolean;
  regulatoryCensured?: string | boolean;
  regulatoryInvolvedInAnyActivitiesRequiringAuthorization?: string | boolean;
  regulatorySubjectToAnyDisqualification?: string | boolean;
  typeOfBusinessLicence?: string;
  hmrcMlrNumber?: string;
  hmrcRegistrationExpiryDate?: string;
  fcaNumber?: string;
  icoNumber?: string;
  numberOfBranches?: string;
  numberOfAgents?: string;
  mlroDetails?: string;
  complianceConsultantDetails?: string;
  accountantDetails?: string;
  itProviderDetails?: string;
  payoutPartnerDetails?: string;
  hasTransactionMonitoring?: string | boolean;
  transactionMonitoringKind?: string;
  hasScreening?: string | boolean;
  screeningKind?: string;
  hasRegulatoryInvestigation?: string | boolean;
  regulatoryInvestigationDetails?: string;
  visitedByAuthorities?: string;
  visitedByAuthoritiesDetails?: string;
  hasOnboardCorporateClients?: string | boolean;
  hasOnboardMsb?: string | boolean;
  hasPaymentsForCharities?: string | boolean;
  hasChangesInManagementOrOwnership?: string | boolean;
  changesInManagementOrOwnershipDetails?: string;
  hasWorkingWithAnyOtherIpsp?: string | boolean;
  workingWithAnyOtherIpspDetails?: string;
  hasAuditedByExternalCompliance?: string | boolean;
  auditedByExternalComplianceDetails?: string;
  contactPersonName?: string;
  phoneNumber?: string;
  faxNumber?: string;
  contactEmail?: string;
  legalEntityIdCode?: string;
  isPepOrAssociate?: string | boolean;
  nameOfPep?: string;
  relToCustomerId?: string;
  relToPep?: string;
  orgEmployerAndPosition?: string;
  positionHeldCountryId?: string;
  politicalFunctionPeriodFrom?: string;
  politicalFunctionPeriodTo?: string;
  abilityMoveGovernmentFunds?: boolean | string;
  abilityMoveGovernmentFundsDetails?: string;
  belongsToGroup?: boolean | string;
  parentCompanyName?: string;
  parentCompanyCountryId?: string;
  subsidiariesInfo?: string;
  mainActivities?: string;
  groupWealthSourceIds?: string[];
  estimatedTotalWealthEuro?: string | number;
  customerAppliesKycProcedures?: string | boolean;
  hasBusinessActivity?: string | boolean;
  businessActivitiesIds?: string[];
  customersMainClientsIds?: string[];
  customersMainClientsOther?: string;
  salesChannelsIds?: string[];
  salesChannelsOther?: string;
  numberOfEmployees?: string | number;
  hasAccountsInOtherFinancialInstitutions?: string | boolean;
  otherFinancialInstitutions?: string;
  sourcesOfFundsIds?: string[];
  sourcesOfFundsLoanBusinessPartner?: string;
  sourcesOfFundsLoanCreditInstitution?: string;
  sourcesOfFundsOther?: string;
  sourceOfWealthIds?: string[];
  sourceOfWealthOther?: string;
  estimatedTotalWealth?: string | number;
  annualIncome?: string | number;
  sourcesOfAnnualIncomes?:
    | {
        sourceOfAnnualIncomeId: string;
        percentage?: string | number;
      }[]
    | string;
  sourcesOfAnnualIncomesOther?: string;
  accountTypeIds?: string[];
  tradingName?: string;
  questionnairesAccountsCurrencies?: string[];
  questionnairesAccountsFlows?: string[];
  questionnairesExpectedFundsOrigins?: string[];
  questionnairesAffiliatedCompanies?: string[];
  otherNationalitiesIds?: string[];
  maritalStatus?: string;
  spouseFullName?: string;
  natureOfBusinessActivity?: string;
  nameOfCompany?: string;
  ownership?: number | string;
  requiresSourcesOfIncomes?: string | boolean;
  sourcesOfIncomes?:
    | {
        sourceId: string;
        value: string | number;
      }[]
    | string;
  sourcesOfIncomesOther?: string;
  sourcesOfIncomesBorrowedOther?: string;
  requiresSourceAndSizeOfWealth?: string | boolean;
  sourcesAndSizesOfWealth?:
    | {
        id?: string;
        sourceId: string;
        value: string | number;
      }[]
    | string;
  sourcesAndSizesOfWealthOther?: string;
  sourcesAndSizesOfWealthBorrowedOther?: string;
  requiresNetWorth?: string | boolean;
  netWorthAmount?: string | number;
  groupWealthSourceOther?: string;
  secondPassportExpiryDate?: string;
  secondPassportIssueCountryId?: string;
  secondPassportIssueDate?: string;
  secondPassportNumber?: string;
  isUSCitizen?: string | boolean;
  questionnairesCrsFatcaDeclarations?: string;
  homePhoneNumber?: string;
  emails?: string[];
  hasAffiliatedCompanies?: string | boolean;
}

export interface ExternalOrganization {
  organizationId: string;
  organizationType: OrganizationType;
  externalId: string;
  active: boolean;
  id: string;
  createdAt: string;
}

export interface CustomerProps {
  accounts: {
    edges: {
      node: Account & { visibleToClient: boolean; statusLogs: AccountLogs[] };
    }[];
  };
  application?: {
    id: string;
  };
  ids?: string;
  url?: string;
  search?: string;
  address1: string;
  address2: string;
  applicationId: string;
  statusChangeReason?: string;
  businessAddress1: string;
  businessAddress2: string;
  businessAddressSameAsRegisteredAddress: boolean;
  businessCity: string;
  businessCountryId: string;
  businessDescription: string;
  businessLicense: string;
  businessPostalCode: string;
  city: string;
  clientActivityId: string;
  typeOfActivityDescription?: string;
  clientScheme?: {
    id: string;
    name: string;
  };
  clientsScoreResult: CustomerScore;
  customers?: SupplierCustomersProps[];
  suppliers?: SupplierCustomersProps[];
  companyName: string;
  companyNumber: string;
  correspondenceAddress1: string;
  correspondenceAddress2: string;
  correspondenceAddressSameAsRegisteredAddress: boolean;
  correspondenceCity: string;
  correspondenceCountryId: string;
  correspondencePostalCode: string;
  countriesOfInterestIds: string[];
  idType?: string;
  idNumber?: string;
  idExpDate?: string;
  merchantId?: string;
  country: {
    id: string;
    name: string;
    riskScore: number;
  };
  customFees?: FeeItem[];
  countryId: string;
  countryOfIncorporationId: string;
  dateOfIncorporation: string;
  deliveryChannelId: string;
  employmentStatus: EmploymentStatus;
  firstName: string;
  id: string;
  isUSResident: boolean;
  lastName: string;
  mccCode: string;
  monthlyMaxValueIncomingPaymentRangeId: string;
  monthlyMaxValueOutgoingPaymentRangeId: string;
  monthlyNumberIncomingTransactionsRangeId: string;
  monthlyNumberOutgoingTransactionsRangeId: string;
  monthlyTurnoverRangeId: string;
  monthlyValueIncomingTransactionsRangeId: string;
  monthlyValueOutgoingTransactionsRangeId: string;
  nationalityId: string;
  operationStatus: string;
  organizationFeatures: string;
  participatesInPolitics: boolean;
  postalCode: string;
  registeredAddress1: string;
  registeredAddress2: string;
  registeredCity: string;
  registeredCountryId: string;
  registeredPostalCode: string;
  riskProfile: { totalRiskScore: number };
  sicCodes: string[];
  questionnaire?: Questionnarie;
  vatNumber?: string;
  taxNumber?: string;
  amlCtfComplianceProgram?: boolean | string | null;
  requiredCurrenciesCryptoId?: string[];
  purposeOfAccount?: string;
  expectedTypeOfClients?: string[];
  pspIntegrationOrganization?: ExternalOrganization;
  birthCountryId?: string;
  placeOfBirth?: string;
  nameOfEmployer?: string;
  employeePosition?: string;
  countryOfEmployerId?: string;
  officials?: {
    edges: {
      node: {
        authLevel: string;
        companyOfficialType: string;
        createdAt: string;
        deletedAt: string;
        id: string;
        natureOfControl: NatureOfControlEnum;
        organizationId: string;
        percentage: string;
        position: string;
        status: string;
        user: {
          status: string;
          profile: {
            firstName: string;
            lastName: string;
            dob: string;
          };
        };
        userId: string;
      };
    }[];
  };
  sourcesOfFunds: {
    edges: {
      node: {
        riskScoreRange: {
          name: string;
          rangeStart: number;
          rangeEnd: number;
          action: string;
          id: string;
        };
        name: string;
        riskScoreRangeId: string;
        riskScore: number;
        id: string;
      };
    }[];
  };
  products: {
    edges: {
      node: {
        name: string;
        id: string;
      };
    }[];
  };
  status: CustomerStatus;
  tariffId: string;
  tariff: {
    name: string;
    type: string;
    status: string;
    isActive: boolean;
    isDefault: boolean;
    riskScoreRangeId: string;
    fees: FeeItem[];
    id: string;
    createdAt: string;
    updatedAt: string;
  };
  type: OrganizationType;
  user: {
    id: string;
    totpEnabled?: boolean;
    profile?: {
      dob: string;
      additionalEmail?: string;
    };
    email: string;
    phone: string;
  };
  userId: string;
  dob?: string;
  websiteLink: string;
  representativeOfOrganizations: Company[];
  render?: Record<string, React.JSX.Element>;
}

export enum CustomerScoreUpdateStatus {
  QUEUED = 'QUEUED',
  FAIL = 'FAIL',
  SUCCESS = 'SUCCESS',
}

export interface CustomerScoreQueuedProps {
  id: string;
  organizationId: string;
  status: CustomerScoreUpdateStatus;
}

export interface TransactionTypeProps {
  active: boolean;
  createdAt: string;
  id: string;
  name: TransactionTypeEnum;
}

export interface StatementListItemProps {
  id: string;
  currencyId: string;
  sum: number;
  type: TransactionTypeEnum;
  transferDate?: string;
  counterpartyAccountNumber?: string;
  counterpartyFullName?: string;
  counterpartyAccountName?: string;
  counterpartyFirstName?: string;
  counterpartyLastName?: string;
  documentNumber?: string;
  currency?: Currency;
  generalLedgerTransactionId?: string;
  transactionId?: string;
  creditSum?: number;
  debitSum?: number;
  balance?: number;
  url?: string;
  render?: Record<string, React.JSX.Element>;
}

export enum AmlAction {
  AML_REVIEW = 'AML_REVIEW',
  OPS_REVIEW = 'OPS_REVIEW',
  NONE = 'NONE',
}

export interface RiskScoreRange {
  name: string;
  rangeStart: number;
  rangeEnd: number;
  id: string;
  action: AmlAction;
}

export interface Balance {
  accountId: string;
  currencyId: string;
  currency?: { name: string };
  id: string;
  value: number;
  overdraftEnabled?: boolean;
  overdraftValue?: number;
  currencyName?: string;
}

export interface PspPartner {
  id: string;
  companyName: string;
  description?: string;
  pspIntegrationId?: string;
}

export interface TopUpVisibility {
  organizationId: string;
  id: string;
  visible: boolean;
  pspPartnerId: string;
}

export interface TopUpInstruction {
  beneficiary: string;
  beneficiaryAddress: string;
  beneficiaryAccountNumber: string[];
  beneficiaryBankName: string;
  beneficiaryBankBIC: string;
  purposeOfPayment: string;
  paymentType: string;
  intermediaryBankName?: string;
  intermediaryBankSwift?: string;
  beneficiaryBankABA?: string;
  accountOfCorrespondentWithIntermediaryBank?: string;
  casp?: {
    id: string;
    name: string;
  };
}

export interface TopUpInstructions {
  items: TopUpInstruction[];
}

export interface NewCofProps {
  userId?: string;
  email: string;
  dob?: string;
  firstName: string;
  lastName: string;
  phone?: string;
  organizationId?: string;
  position?: string;
  percentage?: string;
  natureOfControl?: NatureOfControlEnum;
  companyOfficialType?: string;
  signatureType?: SignatureType;
  havingControl?: boolean;
  accessRight?: AccessRight;
}

export interface ProblemTransactionCofProps {
  userId: string;
  organizationId: string;
  status: string;
  position: string;
  companyOfficialType: string;
  id: string;
  user: {
    status: string;
    totEnabled: boolean;
    serviceRole: string[];
    organization?: {
      id?: string;
    };
    profile: {
      firstName: string;
      lastName: string;
      dob: string;
      id: string;
      createdAt: string;
      updatedAt: string;
      deletedAt: string;
    };
  };
}

export interface OngoingMonitring {
  createdAt: string;
  id: string;
  organizationId: string;
  updatedAt: string;
  blocklistNumber?: string;
  warnings: {
    edges: { node: TransactionWarning }[];
    pageInfo: Pagination;
  };
  warningsGroupsBlacklists: {
    edges: {
      node: {
        blacklist: {
          name: string;
        };
        blacklistId: string;
        createdAt: string;
        deletedAt: string;
        id: string;
        updatedAt: string;
        warningGroupId: string;
        warningsCount: string;
      };
    }[];
    pageInfo: Pagination;
  };
  render?: Record<string, React.JSX.Element>;
}

export enum OngoingStatus {
  QUEUED = 'QUEUED',
  PROCESSING = 'PROCESSING',
  SUCCESS = 'SUCCESS',
  FAIL = 'FAIL',
}

export interface OngoingCheckingStatus {
  organizationId?: string;
  applicationId?: string;
  status: OngoingStatus;
  id: string;
  createdAt: string;
}

export enum ClientType {
  INDIVIDUAL = 'INDIVIDUAL',
  CORPORATE = 'CORPORATE',
}

export interface ClientScheme {
  name: string;
  primaryId?: string;
  transactionRiskScoringEnabled: boolean;
  transactionMonitoringEnabled: boolean;
  transactionBlacklistEnabled: boolean;
  transactionAgreementEnabled: boolean;
  currencies: {
    edges: any[];
  };
  id: string;
}

export interface ApplicationScheme {
  name: string;
  type: ApplicationSchemeType;
  active: boolean;
  visible: boolean;
  deliveryChannelVisible?: boolean;
  deliveryChannelTitle?: string;
  deliveryChannelId?: string;
  countriesOfInterestActive?: boolean;
  countriesOfInterestTitle?: string;
  clientSchemeId?: string;
  deliveryChannel: {
    name: string;
    active: boolean;
    riskScoreRangeId: string;
    riskScore: number;
    clientType: ClientType;
    id: string;
  };
  kycProvider: string;
  kycRequired: boolean;
  agreementsActive: boolean;
  agreementsRequired: boolean;
  taxNumberActive: boolean;
  taxNumberRequired: boolean;
  vatNumberActive: boolean;
  vatNumberRequired: boolean;
  accountantDetailsActive?: boolean;
  auditedByExternalComplianceActive?: boolean;
  changesInManagementOrOwnershipActive?: boolean;
  complianceConsultantDetailsActive?: boolean;
  customersEnabled?: boolean;
  fcaNumberActive?: boolean;
  hmrcMlrNumberAndExpiryDateActive?: boolean;
  icoNumberActive?: boolean;
  itProviderDetailsActive?: boolean;
  mlroDetailsActive?: boolean;
  numberOfAgentsActive?: boolean;
  numberOfBranchesActive?: boolean;
  onboardCorporateClientsActive?: boolean;
  onboardMsbActive?: boolean;
  paymentsForCharitiesActive?: boolean;
  payoutPartnerDetailsActive?: boolean;
  hmrcMlrNumberActive?: true;
  hmrcMlrNumberRequired?: true;
  hmrcRegistrationExpiryDateActive?: true;
  hmrcRegistrationExpiryDateRequired?: true;
  regulatoryInvestigationActive?: boolean;
  screeningActive?: boolean;
  suppliersEnabled?: boolean;
  transactionMonitoringActive?: boolean;
  typeOfBusinessLicenceActive?: boolean;
  visitedByAuthoritiesActive?: boolean;
  workingWithAnyOtherIPSPActive?: boolean;
  civilActive?: boolean;
  criminalOfAnyActive?: boolean;
  lastThreeYearsAddressHistoryActive?: boolean;
  nationalInsuranceNumberOfDirectorsActive?: boolean;
  regulatoryActive?: boolean;
  businessAndEmploymentActive?: boolean;
  clientScheme: ClientScheme;
  amlCtfComplianceProgramActive?: boolean;
  amlCtfComplianceProgramRequired?: boolean;
  expectedTypeOfClientsActive?: boolean;
  expectedTypeOfClientsRequired?: boolean;
  purposeOfAccountActive?: boolean;
  purposeOfAccountRequired?: boolean;
  requiredCurrenciesCryptoActive?: boolean;
  requiredCurrenciesCryptoRequired?: boolean;
  customersNameTitle?: string;
  customersTitle?: string;
  suppliersAndCustomersTitle?: string;
  suppliersNameTitle?: string;
  suppliersTitle?: string;
  documentsCategories: {
    edges: {
      node: {
        createdAt: string;
        deletedAt: string;
        id: string;
        name: string;
        required: boolean;
        updatedAt: string;
      };
    }[];
  };
  id: string;
  render?: Record<string, React.JSX.Element>;
}

export enum PspPartnerTypeEnum {
  CREDIT = 'CREDIT',
  PAYMENT = 'PAYMENT',
  EMONEY = 'EMONEY',
}

export enum PspPartnerLocationEnum {
  DOMESTIC = 'DOMESTIC',
  FOREIGN = 'FOREIGN',
}

export enum CurrencyType {
  ALL = 'ALL',
  NONE = 'NONE',
  SPECIFIC = 'SPECIFIC',
}

export enum ConnectionTypeEnum {
  CSM = 'CSM',
  VIRTUAL_IBAN = 'VIRTUAL_IBAN',
  NO_IBAN = 'NO_IBAN',
}

export interface ConnectionType {
  pspIntegrationPaymentId: string;
  connectionType: ConnectionTypeEnum;
  id: string;
}

export interface PspIntergrationCurrency {
  pspIntegrationPaymentId: string;
  currencyId: string;
  currency: Currency;
  id: string;
}

export interface PaymentsPartnersSetting {
  pspIntegrationPaymentId: string;
  pspIntegrationPayment: PspIntegrationPayment;
  pspPartnerId: string;
  pspPartner: ExternalPspPartner;
  bic?: string;
  sortCode?: string;
  id: string;
}

export interface PspIntegrationPayment {
  pspIntegrationId: string;
  paymentsTypes: string[];
  currencyType: CurrencyType;
  connectionsTypes: ConnectionType[];
  currencies: PspIntergrationCurrency[];
  paymentsPartnersSettings: PaymentsPartnersSetting[];
  id: string;
}

export enum ExportFileTypeEnum {
  HARBOUR_HILLS_B2B_LITE = 'HARBOUR_HILLS_B2B_LITE',
  SPITAMEN = 'SPITAMEN',
}

export interface ExternalPspPartner {
  partnerType: PspPartnerTypeEnum;
  location: PspPartnerLocationEnum;
  paymentsTypes: string[];
  companyName: string;
  legalName: string;
  swift?: string;
  sortCode?: string;
  pspIntegrationId?: string;
  caspId?: string;
  pspIntegration?: {
    name: string;
    pspIntegrationPayments: PspIntegrationPayment[];
    id: string;
    externalCustomersSupported?: boolean;
  };
  address: string;
  managerContacts?: string;
  onlineBankLink?: string;
  description?: string;
  active: boolean;
  visibleToClients: boolean;
  countryId: string;
  country: Country;
  intermediaryBankName?: string;
  intermediaryBankSwift?: string;
  accountOfCorrespondentWithIntermediaryBank?: string;
  beneficiary?: string;
  exportFileType?: ExportFileTypeEnum;
  id: string;
}

export interface RateSource {
  name: string;
  description: string;
  id: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string;
}

export interface PspIntegration {
  id: string;
  name: string;
  externalCustomersSupported: boolean;
  pspIntegrationPayments: {
    id: string;
    currencies: {
      id: string;
      currency: {
        id: string;
        name: string;
      };
    }[];
  }[];
}

export interface PspIntegrationOrganization {
  externalId: string;
  name: string;
  organizationType: OrganizationType;
}

export type UserAccountProps = Account & {
  visibleToClient: boolean;
  statusLogs: AccountLogs[];
  overdraftEnabled?: boolean;
};

export enum CounterPartyEnum {
  ANY = 'ANY',
  SAME = 'SAME',
  NEW = 'NEW',
}

export enum TransactionMonitoringRuleEntity {
  TRANSACTION = 'TRANSACTION',
  PAYMENT_ORDER = 'PAYMENT_ORDER',
}

export interface TransactionMonitoringRule {
  transactionTypeId: string;
  transactionType: {
    name:
      | TransactionTypeEnum.INTERNAL
      | TransactionTypeEnum.INCOMING
      | TransactionTypeEnum.OUTGOING;
  };
  name: string;
  number: number;
  volumeFrom: number;
  volumeTo: number;
  periodOfTimeInHours: number;
  counterParty: CounterPartyEnum;
  clientType: ClientType;
  active: boolean;
  action: string;
  id: string;
  url?: string;
  createdAt: string;
  entity?: TransactionMonitoringRuleEntity;
  paymentOrderType?: string;
  paymentType?: string;
  clientRiskScoreRangeIds?: string[];
  organizationIds?: string[];
  counterpartyCountryIds?: string[];
  counterpartyCountryRiskScoreRangeIds?: string[];
  counterpartyBankCountryIds?: string[];
  counterpartyBankCountryRiskScoreRangeIds?: string[];
  currencyIds?: string[];
  singleTransactionAmountFrom?: string | number | null;
  singleTransactionAmountTo?: string | number | null;
  numberTo?: number;
  render?: Record<string, React.JSX.Element>;
}
